import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Chip
} from '@mui/material';
import { ArrowBack, Class as ClassIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Classes = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const response = await teacherAPI.getClasses();
        if (isMounted) {
          setClasses(response.data.classes || []);
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        if (isMounted) {
          setClasses([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          My Classes
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/teacher/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>

      {classes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="h6">
            No classes assigned to you yet
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Please contact your principal to assign classes
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {classes.map((cls) => (
            <Grid item xs={12} md={6} lg={4} key={cls.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <ClassIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {cls.name}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5}>
                        {cls.grade && <Chip label={`Grade ${cls.grade}`} size="small" />}
                        {cls.section && <Chip label={`Section ${cls.section}`} size="small" />}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Students: {cls.student_count || 0}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/teacher/classes/${cls.id}/manage`)}
                  >
                    Manage Class
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Classes;
